/**
 * Access-control (hardware bridge) + multi-location validators (plan 12, Run 16).
 * Devices (registered turnstile/RFID/smart-lock hardware) and hardware scan events
 * that the access-bridge Edge Fn translates into a check-in via the SAME
 * insert_checkin_with_decrement RPC as /checkins (source='turnstile' | 'card').
 */
import { z } from 'zod';

import { plan_type } from '../enums.js';

/** Driver registry IDs — concrete hardware drivers behind the bridge interface. */
export const accessDriverSchema = z.enum(['mock', 'wiegand_bridge', 'osdp_bridge', 'ble_relay']);

/** "HH:MM" or "HH:MM:SS" 24h clock time (off-peak window bounds). */
const clockTimeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, 'expected HH:MM[:SS] 24h time');

/** Hardware protocol the device speaks to the on-prem relay. */
export const accessProtocolSchema = z.enum(['wiegand', 'osdp', 'ble_relay', 'mock']);

/** Physical device class. */
export const accessDeviceTypeSchema = z.enum(['turnstile', 'rfid_reader', 'smart_lock']);

/**
 * The two hardware-scan check-in sources. Mirrors public.checkin_source — a
 * hardware scan is always 'turnstile' or 'card' (never app_qr/manual).
 */
export const accessCheckinSourceSchema = z.enum(['turnstile', 'card']);

/** Both off-peak bounds must be present (or both absent). */
const offPeakPaired = (v: { off_peak_start?: unknown; off_peak_end?: unknown }): boolean =>
  (v.off_peak_start == null) === (v.off_peak_end == null);
const offPeakPairedMsg = {
  message: 'off_peak_start and off_peak_end must be set together',
  path: ['off_peak_end'] as (string | number)[],
};

/** Base shape (no cross-field refine) so .partial() works for the update schema. */
const accessDeviceBase = z.object({
  location_id: z.string().uuid(),
  label: z.string().min(1).max(120),
  device_type: accessDeviceTypeSchema.default('turnstile'),
  protocol: accessProtocolSchema.default('mock'),
  driver: accessDriverSchema.default('mock'),
  checkin_source: accessCheckinSourceSchema.default('turnstile'),
  serial_number: z.string().max(120).optional(),
  ip_address: z.string().max(64).optional(),
  port: z.number().int().min(1).max(65535).optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  is_active: z.boolean().default(true),
  unmanned_mode: z.boolean().default(false),
  /** ML-03 cross-branch plan restriction: null/omitted = any plan may use this
   *  device; else only these plan_types are granted (others -> denial). */
  allowed_plan_types: z.array(z.enum(plan_type)).min(1).optional(),
  /** AC-04 off-peak window (tenant timezone). Both bounds required together;
   *  entry outside [start, end] is denied. Window may wrap past midnight. */
  off_peak_start: clockTimeSchema.optional(),
  off_peak_end: clockTimeSchema.optional(),
});

/** Register / update a hardware device (staff: owner,manager). */
export const AccessDeviceCreateSchema = accessDeviceBase.refine(offPeakPaired, offPeakPairedMsg);

export const AccessDeviceUpdateSchema = accessDeviceBase
  .partial()
  .refine(offPeakPaired, offPeakPairedMsg);

/**
 * A hardware scan reported by the bridge. The bridge resolves a presented
 * credential to a member_id (RFID UID / QR token) BEFORE calling — the Edge Fn
 * never receives raw PII it cannot trust. client_event_id is the idempotent
 * hardware replay key (Idempotency-Key header, exactly-once via I5).
 */
export const AccessScanSchema = z.object({
  device_id: z.string().uuid(),
  member_id: z.string().uuid(),
  /** ISO device timestamp; used (not now()) for the checkin time (plan 12 §6). */
  occurred_at: z.string().datetime({ offset: true }),
  client_event_id: z.string().uuid(),
  /** Optional credential reference for audit (hash, never raw UID). */
  credential_ref: z.string().max(128).optional(),
});

/** Device status heartbeat the bridge may push (updates last_seen_at/last_status). */
export const AccessDeviceStatusSchema = z.object({
  device_id: z.string().uuid(),
  online: z.boolean(),
  door_state: z.enum(['locked', 'unlocked', 'ajar', 'unknown']).optional(),
  occurred_at: z.string().datetime({ offset: true }),
  details: z.record(z.string(), z.unknown()).optional(),
});

export type AccessDeviceCreateInput = z.infer<typeof AccessDeviceCreateSchema>;
export type AccessDeviceUpdateInput = z.infer<typeof AccessDeviceUpdateSchema>;
export type AccessScanInput = z.infer<typeof AccessScanSchema>;
export type AccessDeviceStatusInput = z.infer<typeof AccessDeviceStatusSchema>;
